import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Users } from 'lucide-react';
import { Supplier, CreateSupplierRequest } from '../../types';
import { supplierService } from '../../services/supplierService';
import { customToast } from '../../utils/customToast';
import { useTheme } from '../../contexts/ThemeContext';

interface SupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  supplier: Supplier | null;
  mode: 'create' | 'edit' | 'view';
}

const SupplierModal: React.FC<SupplierModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  supplier,
  mode
}) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [formData, setFormData] = useState<CreateSupplierRequest>({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    tax_id: '',
    website: '',
    payment_terms: '',
    supplier_type: 'manufacturer',
    notes: '',
    is_active: true
  });

  useEffect(() => {
    setIsDataLoading(true);
    if (supplier) {
      setFormData({
        name: supplier.name,
        contact_person: supplier.contact_person || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        city: supplier.city || '',
        country: supplier.country || '',
        tax_id: supplier.tax_id || '',
        website: supplier.website || '',
        payment_terms: supplier.payment_terms || '',
        supplier_type: supplier.supplier_type,
        notes: supplier.notes || '',
        is_active: supplier.is_active
      });
    } else {
      // Reset form for create mode
      setFormData({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        tax_id: '',
        website: '',
        payment_terms: '',
        supplier_type: 'manufacturer',
        notes: '',
        is_active: true
      });
    }
    setIsDataLoading(false);
  }, [supplier, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      customToast.error('Supplier name is required');
      return;
    }

    // Validate supplier ID for edit mode
    if (mode === 'edit' && (!supplier || !supplier.id)) {
      customToast.error('Invalid supplier data. Please refresh and try again.');
      return;
    }

    try {
      setLoading(true);
      
      if (mode === 'create') {
        await supplierService.createSupplier(formData);
        customToast.success('Supplier created successfully!');
      } else if (mode === 'edit' && supplier?.id) {
        await supplierService.updateSupplier(supplier.id, formData);
        customToast.success('Supplier updated successfully!');
      }
      
      onSuccess();
    } catch (error: any) {
      console.error('Error saving supplier:', error);
      customToast.error(error.response?.data?.message || 'Failed to save supplier. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  if (!isOpen) return null;

  const isViewMode = mode === 'view';

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 150);
  };

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}
      onClick={handleClose}
    >
      <div 
        className={`w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${isClosing ? 'animate-scaleOut' : 'animate-scaleIn'} ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                <Users className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                  {mode === 'create' && 'Add New Supplier'}
                  {mode === 'edit' && 'Edit Supplier'}
                  {mode === 'view' && 'Supplier Details'}
                </h2>
                <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {mode === 'create' && 'Enter supplier information'}
                  {mode === 'edit' && 'Update supplier details'}
                  {mode === 'view' && 'View supplier information'}
                </p>
              </div>
            </div>
            <button onClick={handleClose} className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Form Body */}
        <div className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}>
          {isDataLoading ? (
            // Loading Skeleton
            <div className="p-4 sm:p-5 md:p-6 space-y-6">
              {/* Section 1 Skeleton */}
              <div className="space-y-4">
                <div className={`h-6 w-40 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(3)].map((_, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className={`h-4 w-24 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                      <div className={`h-10 w-full rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 2 Skeleton */}
              <div className="space-y-4">
                <div className={`h-6 w-40 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(4)].map((_, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className={`h-4 w-24 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                      <div className={`h-10 w-full rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes Section Skeleton */}
              <div className="space-y-4">
                <div className={`h-4 w-20 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
                <div className={`h-24 w-full rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`} />
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 sm:p-5 md:p-6 space-y-5">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className={`text-base font-medium flex items-center space-x-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                <Users className="w-5 h-5" />
                <span>Basic Information</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Supplier Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} disabled={isViewMode} required className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 placeholder-gray-500 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`} placeholder="Enter supplier name" />
                </div>
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Contact Person</label>
                  <input type="text" name="contact_person" value={formData.contact_person} onChange={handleChange} disabled={isViewMode} className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 placeholder-gray-500 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`} placeholder="Enter contact person" />
                </div>
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Supplier Type *</label>
                  <select name="supplier_type" value={formData.supplier_type} onChange={handleChange} disabled={isViewMode} required className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`}>
                    <option value="manufacturer">Manufacturer</option>
                    <option value="distributor">Distributor</option>
                    <option value="wholesaler">Wholesaler</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className={`text-base font-medium flex items-center space-x-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                <Users className="w-5 h-5" />
                <span>Contact Information</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={isViewMode} className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 placeholder-gray-500 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`} placeholder="supplier@example.com" />
                </div>
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Phone</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} disabled={isViewMode} className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 placeholder-gray-500 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`} placeholder="Enter phone number" />
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="space-y-4">
              <h3 className={`text-base font-medium flex items-center space-x-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                <Users className="w-5 h-5" />
                <span>Location Information</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Address</label>
                  <input type="text" name="address" value={formData.address} onChange={handleChange} disabled={isViewMode} className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 placeholder-gray-500 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`} placeholder="Enter address" />
                </div>
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>City</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} disabled={isViewMode} className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 placeholder-gray-500 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`} placeholder="Enter city" />
                </div>
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Country</label>
                  <input type="text" name="country" value={formData.country} onChange={handleChange} disabled={isViewMode} className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 placeholder-gray-500 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`} placeholder="Enter country" />
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="space-y-4">
              <h3 className={`text-base font-medium flex items-center space-x-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                <Users className="w-5 h-5" />
                <span>Business Information</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Tax ID</label>
                  <input type="text" name="tax_id" value={formData.tax_id} onChange={handleChange} disabled={isViewMode} className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 placeholder-gray-500 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`} placeholder="Enter tax ID" />
                </div>
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Website</label>
                  <input type="url" name="website" value={formData.website} onChange={handleChange} disabled={isViewMode} className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 placeholder-gray-500 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`} placeholder="https://example.com" />
                </div>
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Payment Terms</label>
                  <input type="text" name="payment_terms" value={formData.payment_terms} onChange={handleChange} disabled={isViewMode} className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 placeholder-gray-500 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`} placeholder="e.g., Net 30" />
                </div>
                <div className="md:col-span-2">
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Notes</label>
                  <textarea name="notes" value={formData.notes} onChange={handleChange} disabled={isViewMode} rows={3} className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation resize-none ${theme === 'dark' ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}` : `bg-white text-gray-900 placeholder-gray-500 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''} border-slate-300`}`} placeholder="Add any additional notes" />
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center space-x-2">
              <input type="checkbox" id="is_active" name="is_active" checked={formData.is_active} onChange={handleChange} disabled={isViewMode} className={`rounded border-gray-300 text-blue-600 focus:ring-0 ${isViewMode ? 'opacity-60 cursor-not-allowed' : ''}`} />
              <label htmlFor="is_active" className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>Active Supplier</label>
            </div>
          </form>
          )}
        </div>

        {/* Footer */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'}`}>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button type="button" onClick={handleClose} disabled={loading} className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 touch-manipulation ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} disabled:opacity-50 disabled:cursor-not-allowed`}>
              {isViewMode ? 'Close' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button type="submit" disabled={loading} onClick={handleSubmit} className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 touch-manipulation ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'} disabled:opacity-50 disabled:cursor-not-allowed`}>
                {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                <span>{loading ? 'Saving...' : (mode === 'create' ? 'Create Supplier' : 'Update Supplier')}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SupplierModal;

